import { type RefObject, useRef, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import {
  MdBlock,
  MdReport,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import noImage from "@/assets/img/no-image.svg";
import { BOARD_CONFIG } from "@/config/browserBoard";
import type { Thread } from "@/entities/thread";
import { getImageUrl, getThreadTitle } from "@/entities/thread";
import { ThreadActionPopover } from "@/features/catalog/components/actions/ThreadActionPopover";
import {
  type CatalogContextMenuEffect,
  createCatalogContextMenuMediator,
  isCatalogContextMenuEvent,
} from "@/features/catalog/mediators/catalogContextMenuMediator";
import type {
  CatalogEvent,
  CatalogEventNotifier,
} from "@/features/catalog/mediators/catalogEvents";
import { isCatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import { useNgStore } from "@/features/ng-filter/stores/ngStore";
import { useIsDesktop } from "@/shared/hooks";
import { useDelMutation } from "@/shared/hooks/useDelMutation";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
} from "@/shared/ui/component-hierarchy/hooks";
import { Button } from "@/shared/ui/form";
import { Modal } from "@/shared/ui/overlay";

interface Props {
  thread: Thread;
  isOpen: boolean;
  onClose: () => void;
  /** PC版ポップオーバーの位置基準となる起点要素("…"ボタン)への参照。 */
  anchorRef?: RefObject<HTMLElement | null>;
}

export const ThreadContextMenu: React.FunctionComponent<Props> = ({
  thread,
  isOpen,
  onClose,
  anchorRef,
}: Props) => {
  const isDesktop = useIsDesktop();
  const { hiddenThreadIds, hideThread, unhideThread, addNgImage, addNgTitle } =
    useNgStore();
  const removeFromHistory = useHistoryStore((state) => state.removeFromHistory);
  const { mutate: del } = useDelMutation();
  const title = getThreadTitle(thread);
  const imageHash = thread.opPost.attachment?.ngHash ?? null;
  const effectRunner = useRef<(effect: CatalogContextMenuEffect) => void>(
    (): void => undefined,
  );
  effectRunner.current = (effect: CatalogContextMenuEffect): void => {
    switch (effect.type) {
      case "delete":
        if (confirm("スレッドをdelしますか？")) del(thread.opPost.id);
        return;
      case "hide":
        hideThread(thread.id);
        return;
      case "show":
        unhideThread(thread.id);
        return;
      case "ng-title":
        addNgTitle(title);
        alert(`「${title.slice(0, 20)}...」をNGスレ文に追加しました`);
        return;
      case "ng-image": {
        const result = imageHash
          ? addNgImage(imageHash, "スレ画像")
          : { success: false, message: "画像ハッシュが取得できませんでした" };
        alert(result.message);
        return;
      }
      case "remove-history":
        removeFromHistory(thread.b ?? BOARD_CONFIG.boardKey, thread.id);
        alert("履歴から削除しました");
        return;
      case "close":
        onClose();
    }
  };
  const [mediator] = useState(() =>
    createCatalogContextMenuMediator((effect) => effectRunner.current(effect)),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!isCatalogEvent(event) || !isCatalogContextMenuEvent(event)) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const notify: CatalogEventNotifier = (
    event: CatalogEvent,
  ): ReturnType<CatalogEventNotifier> => node.dispatch(event).status;

  return (
    <ComponentScope node={node}>
      <ThreadContextMenuView
        isDesktop={isDesktop}
        isOpen={isOpen}
        anchorRef={anchorRef}
        imageUrl={getImageUrl(thread.opPost.attachment, false)}
        title={title}
        threadId={thread.id}
        replyCount={thread.replyCount}
        isHidden={hiddenThreadIds.includes(thread.id)}
        hasImageHash={imageHash !== null}
        onEvent={notify}
      />
    </ComponentScope>
  );
};

export type ThreadContextMenuViewProps = {
  readonly isDesktop: boolean;
  readonly isOpen: boolean;
  readonly anchorRef?: RefObject<HTMLElement | null>;
  readonly imageUrl: string;
  readonly title: string;
  readonly threadId: string;
  readonly replyCount: number;
  readonly isHidden: boolean;
  readonly hasImageHash: boolean;
  readonly onEvent: CatalogEventNotifier;
};

export function ThreadContextMenuView({
  isDesktop,
  isOpen,
  anchorRef,
  imageUrl,
  title,
  threadId,
  replyCount,
  isHidden,
  hasImageHash,
  onEvent,
}: ThreadContextMenuViewProps): React.ReactElement {
  const close = (): void => {
    onEvent({ type: "catalog/context-close-requested" });
  };
  const content = (
    <>
      <figure className="flex items-center gap-3 p-4 border-b border-border">
        <img
          src={imageUrl}
          alt=""
          className="w-16 h-16 rounded object-cover bg-muted"
          onError={(event: React.SyntheticEvent<HTMLImageElement>): void => {
            (event.target as HTMLImageElement).src = noImage;
          }}
        />
        <figcaption className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">{title}</p>
          <p className="text-sm text-muted-foreground">
            スレッドID: {threadId} / {replyCount}レス
          </p>
        </figcaption>
      </figure>
      <menu className="m-0 list-none space-y-1 p-2">
        <li>
          <Button
            variant="menu"
            onClick={(): void => {
              onEvent({ type: "catalog/context-delete-requested" });
            }}
            icon={<MdReport aria-hidden="true" className="w-5 h-5" />}
          >
            del
          </Button>
        </li>
        <li>
          <Button
            variant="menu"
            onClick={(): void => {
              onEvent({
                type: isHidden
                  ? "catalog/context-show-requested"
                  : "catalog/context-hide-requested",
              });
            }}
            icon={
              isHidden ? (
                <MdVisibility aria-hidden="true" className="w-5 h-5" />
              ) : (
                <MdVisibilityOff aria-hidden="true" className="w-5 h-5" />
              )
            }
          >
            {isHidden ? "表示" : "非表示"}
          </Button>
        </li>
        <li>
          <Button
            variant="menu"
            onClick={(): void => {
              onEvent({ type: "catalog/context-ng-title-requested" });
            }}
            icon={<MdBlock aria-hidden="true" className="w-5 h-5" />}
          >
            NGスレ文追加
          </Button>
        </li>
        {hasImageHash && (
          <li>
            <Button
              variant="menu"
              onClick={(): void => {
                onEvent({ type: "catalog/context-ng-image-requested" });
              }}
              icon={<MdBlock aria-hidden="true" className="w-5 h-5" />}
            >
              画像をNG
            </Button>
          </li>
        )}
        <li>
          <Button
            variant="menu"
            onClick={(): void => {
              onEvent({ type: "catalog/context-history-remove-requested" });
            }}
            icon={<FiTrash2 aria-hidden="true" className="w-5 h-5" />}
          >
            履歴から削除
          </Button>
        </li>
      </menu>
    </>
  );

  if (isDesktop && anchorRef) {
    return (
      <ThreadActionPopover
        isOpen={isOpen}
        onClose={close}
        anchorRef={anchorRef}
        ariaLabel="スレッド操作"
      >
        {content}
      </ThreadActionPopover>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      ariaLabel="スレッド操作"
      position="bottom"
    >
      {content}
      <footer className="p-2 border-t border-border">
        <Button
          variant="ghost"
          onClick={close}
          className="w-full py-3 text-center"
        >
          キャンセル
        </Button>
      </footer>
    </Modal>
  );
}
